import { IAjaxState } from 'Imports';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { TelematicProviderVehicleResponse, VideoEventTypeResponse } from '$Generated/api';

export const getFormattedDeviceResults = (
    deviceResults: IAjaxState<TelematicProviderVehicleResponse[]>,
    filterStartDate: Date,
    filterEndDate: Date,
    includeInactiveVehicles: boolean = true,
) =>
    !deviceResults?.data || deviceResults.data.length == 0
        ? []
        : deviceResults.data
              .filter((dr: TelematicProviderVehicleResponse) => {
                  // Filter out inactive vehicles if includeInactiveVehicles is false
                  return includeInactiveVehicles || dr.isActive;
              })
              .map((dr: TelematicProviderVehicleResponse) => {
                  let secondaryLabel: string | null = null;
                  let extraSearchTerms: string[] | null = null;

                  if (dr.pairings) {
                      const filteredPairings = dr.pairings.filter((p) => {
                          const pairingStart = new Date(p.startDate!);
                          const pairingEnd = p.endDate ? new Date(p.endDate) : null;
                          // Clone date to avoid modify the original object
                          const endDateFilter = new Date(filterEndDate.getTime());
                          endDateFilter.setHours(23, 59, 59, 999);
                          return pairingStart <= endDateFilter && (pairingEnd === null || pairingEnd >= filterStartDate);
                      });

                      if (filteredPairings.length > 1) {
                          secondaryLabel = 'Multiple Cameras';
                          extraSearchTerms = filteredPairings.filter((p) => p.cameraSerialNumber).map((p) => p.cameraSerialNumber!);
                      } else if (filteredPairings.length == 1) {
                          secondaryLabel = filteredPairings[0].cameraSerialNumber || null;
                      }
                  }

                  return {
                      label: dr.name || 'Unknown',
                      secondaryLabel: secondaryLabel,
                      value: dr.id || 0,
                      isActive: dr.isActive, // Use the actual isActive value
                      extraSearchTerms: extraSearchTerms || undefined,
                  } as IDropdownItem;
              });

export const getFormattedCompanyLevels = (
    deviceResults: IAjaxState<TelematicProviderVehicleResponse[]>,
    fleetName: string,
): IDropdownItem[] => {
    const vehicles = deviceResults?.data || [];
    if (vehicles.length === 0) {
        return [];
    }

    const normalizedFleetName = (fleetName || '').trim().toLowerCase();

    const reportingLevels = new Map<string, string>();
    for (const vehicle of vehicles) {
        if (vehicle.companyLevelId) {
            reportingLevels.set(vehicle.companyLevelId, vehicle.companyLevelName || vehicle.companyLevelId);
        }
    }

    return Array.from(reportingLevels.entries())
        .map(([id, name]) => ({
            label: name,
            value: id,
            secondaryLabel: name.trim().toLowerCase() === normalizedFleetName ? 'No assigned reporting level' : undefined,
            isActive: true,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
};

export const getFormattedEventTypes = (videoEventTypeResults: IAjaxState<VideoEventTypeResponse[]>) =>
    (videoEventTypeResults.data || []).map((et: any) => ({
        label: et.displayName || 'Unknown',
        value: et.id || 0,
        isActive: true,
    }));

export const getFormattedDrivers = (driversResults: IAjaxState<any>, includeInactiveDrivers: boolean = true) =>
    !driversResults?.data || driversResults.data.length === 0
        ? []
        : driversResults.data
              .filter((d: any) => includeInactiveDrivers || d.isActive)
              .concat({ firstName: 'Unknown', lastName: 'Driver', id: '-1', isActive: true })
              .map((dr: any) => ({
                  label: dr.firstName + ' ' + dr.lastName || 'Unknown',
                  value: dr.id || 0,
                  isActive: dr.isActive || false,
              }));

export const VideoEventScoreOptions = [
    { value: 0, label: 'Unscored' },
    { value: 1, label: '-1 (Low Risk)' },
    { value: 2, label: '-2 (Moderate Risk)' },
    { value: 3, label: '-3 (High Risk)' },
    { value: 4, label: '-4 (Critical)' },
    { value: 6, label: '+1' },
    { value: 7, label: '+2' },
    { value: 8, label: '+3' },
];

export const FlaggedStatusOptions = [
    { value: 1, label: 'Flagged' },
    { value: 0, label: 'Unflagged' },
];
